import { t } from "@lingui/macro"
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    styled,
    Divider,
    Typography,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useEffect } from "react"

import { ConfirmModal } from "src/modals/confirm"
import {
    CopyLegalEntitySettingsStore,
    ICopyLegalEntitySettingsPayload,
} from "src/modals/copy-legal-entity-settings/store"
import { GlobalStore } from "src/store"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { ILegalEntity } from "src/views/client-management/legal-entity/store"

const Wrap = styled(Stack)({ maxHeight: "inherit" })

const List = styled("ul")({
    overflow: "auto",
    margin: 0,
    padding: "0 0 0 1rem",
})

const ListItem = styled("li")({
    listStyle: "none",
})

interface IProps {
    legalEntities: ILegalEntity[]
    settings: ICopyLegalEntitySettingsPayload
    sourceLegalEntityName: string
}

const View = observer((props: IProps) => {
    const store = useStore(CopyLegalEntitySettingsStore)
    const gstore = useStore(GlobalStore)

    useEffect(() => {
        store.init(props.legalEntities, props.settings)
    }, [store, props.legalEntities, props.settings])

    const handleConfirmCopyClick = useCallback(() => {
        gstore.modals.open(
            () => (
                <ConfirmModal
                    onConfirm={async (confirmed) => {
                        if (confirmed) {
                            await store.submit()
                            gstore.modals.pop()
                        }
                    }}
                    title={t`copy-legal-entity-settings-modal.confirm-title`}
                    content={t`copy-legal-entity-settings-modal.confirm-content`}
                />
            ),
            { variant: "slide-up-w600" },
        )
    }, [gstore, store])

    const copyButtonIsDisabled =
        !store.hasSelected() ||
        gstore.loading.is(CopyLegalEntitySettingsStore.LoadingKeys.submit)

    return (
        <Wrap>
            <DialogTitle>
                {t`copy-legal-entity-settings-modal.modal-header`}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>
                    {t({
                        id: "copy-legal-entity-settings-modal.description",
                        values: { name: props.sourceLegalEntityName },
                    })}
                </Typography>
                <List>
                    {store.legalEntities.length > 0 && (
                        <>
                            <ListItem>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={store.allSelected}
                                            indeterminate={
                                                store.hasSelected() &&
                                                !store.allSelected
                                            }
                                            onChange={(_, checked) =>
                                                store.setAllSelected(checked)
                                            }
                                        />
                                    }
                                    label={
                                        <Typography fontWeight={600}>
                                            {t`copy-legal-entity-settings-modal.select-all`}{" "}
                                            ({store.legalEntities.length})
                                        </Typography>
                                    }
                                />
                            </ListItem>
                            <Divider
                                component="li"
                                sx={{ my: 1, listStyle: "none" }}
                            />
                        </>
                    )}
                    {store.legalEntities.map((legalEntity) => (
                        <ListItem key={legalEntity.legalEntityId}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={store.willCopy(legalEntity)}
                                        onChange={() =>
                                            store.toggleCopy(legalEntity)
                                        }
                                    />
                                }
                                label={legalEntity.legalName}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "flex-end", padding: "16px" }}>
                <Button color="info" onClick={() => gstore.modals.pop()}>
                    {t`global.cancel`}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirmCopyClick}
                    disabled={copyButtonIsDisabled}
                >
                    {t`copy-legal-entity-settings-modal.copy-button`}
                </Button>
            </DialogActions>
        </Wrap>
    )
})

export const CopyLegalEntitySettingsModal = observer((props: IProps) => (
    <StoreProvider Store={CopyLegalEntitySettingsStore}>
        <View {...props} />
    </StoreProvider>
))
